import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import api, { API, errMsg } from "@/lib/apiClient";
import { SESSION_EVENT, clearSession, invalidateRequests, saveSession, sessionRevision, validUser } from "@/lib/authSession";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState("");
  const [dark, setDark] = useState(localStorage.getItem("cdoc_theme") === "dark");
  const [actingTenant, setActingTenantState] = useState(() => sessionStorage.getItem("cdoc_acting_tenant") || "");
  const setActingTenant = (tid) => {
    if (tid) sessionStorage.setItem("cdoc_acting_tenant", tid);
    else sessionStorage.removeItem("cdoc_acting_tenant");
    setActingTenantState(tid || "");
  };

  const retryAuth = useCallback(async () => {
    setAuthError("");
    setUser(null);
    const revision = sessionRevision();
    if (!sessionStorage.getItem("cdoc_token") && !sessionStorage.getItem("cdoc_refresh")) {
      setUser(false); return;
    }
    try {
      // The shared interceptor is the only refresh path.
      const { data } = await api.get("/auth/me");
      if (revision !== sessionRevision()) return;
      if (!validUser(data)) { clearSession(); return; }
      setUser(data);
    } catch (error) {
      if (revision !== sessionRevision()) return;
      if ([401, 403].includes(error.response?.status)) clearSession();
      else setAuthError("Non riesco a verificare l'accesso. Controlla la connessione e riprova.");
    }
  }, []);

  useEffect(() => {
    const changed = (event) => {
      setUser(event.detail || false);
      setAuthError("");
      if (!event.detail) setActingTenantState("");
    };
    const storageChanged = (event) => {
      if (event.key === "cdoc_signout") { clearSession(false); return; }
      if (event.key === null || ["cdoc_token", "cdoc_refresh"].includes(event.key)) {
        invalidateRequests();
        retryAuth();
      }
    };
    window.addEventListener(SESSION_EVENT, changed);
    window.addEventListener("storage", storageChanged);
    retryAuth();
    return () => {
      window.removeEventListener(SESSION_EVENT, changed);
      window.removeEventListener("storage", storageChanged);
    };
  }, [retryAuth]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("cdoc_theme", dark ? "dark" : "light");
  }, [dark]);

  const login = async (email, password) => {
    clearSession();
    const revision = sessionRevision();
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveSession(data, revision);
      return { ok: true };
    } catch (e) { return { ok: false, error: errMsg(e, "Credenziali non valide") }; }
  };

  const logout = async () => {
    const token = sessionStorage.getItem("cdoc_token");
    clearSession(); // In-flight refresh cannot restore credentials after logout.
    try {
      if (token) await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      });
    } catch (_) { /* Local session stays closed even if the server is unavailable. */ }
    window.location.href = "/login";
  };

  const isAdmin = validUser(user) && ["SUPER_ADMIN", "TENANT_ADMIN"].includes(user.role);
  const isSuper = validUser(user) && user.role === "SUPER_ADMIN";
  const canManageDocs = validUser(user);
  const canManagePresenze = validUser(user) && (isAdmin || user.permissions?.presenze === true);

  return <AuthContext.Provider value={{ user, authError, retryAuth, login, logout, dark, setDark,
    isAdmin, canEdit: isAdmin, isSuper, canManageDocs, canManagePresenze, actingTenant, setActingTenant }}>
    {children}
  </AuthContext.Provider>;
}
